import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  CircularProgress,
  IconButton,
  Divider,
  Button,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ShareIcon from "@mui/icons-material/Share";
import { getStyles } from "../index"; // Adjust path as needed
import { useSnackbar } from "./SnackbarHandler";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useTheme } from "@mui/material/styles";
import BouncingSpeakers from "./BouncingSpeakers";
import SharePodcastDialog from "./SharePodcastDialog";
import LinkIcon from "@mui/icons-material/Link";
import ShareDialog from "./ShareDialog";

import { formatDate, availableSpeakers } from "./functions";

import LoadingBackdrop from "./LoadingBackdrop";
import ListItemIcon from "@mui/material/ListItemIcon";

import { db } from "./firebase";
import {
  collection,
  addDoc,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  query,
  where,
  limit,
  getDocs,
} from "firebase/firestore";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import PeopleIcon from "@mui/icons-material/People";
import Logger from "./Logger";
import SchoolIcon from "@mui/icons-material/School";

const PodcastDetailsPage = ({ user, isDark }) => {
  const { podcastId } = useParams();
  const [isSharing, setIsSharing] = useState(false);
  const [podcast, setPodcast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogOpenFriends, setShareDialogOpenFriends] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const [shareLinkDialogOpen, setShareLinkDialogOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const styles = getStyles(theme);
  const audioRef = React.useRef(null);

  const [shareAnchorEl, setShareAnchorEl] = useState(null);

  const handleSharePodcast = async ({
    roomId,
    studentIds,
    comment,
    fileUrl,
  }) => {
    // Handle the share logic here
    Logger.log("Sharing podcast:", podcast);
    Logger.log("With room:", roomId);
    Logger.log("To students:", studentIds);
    Logger.log("Comment:", comment);
    setIsSharing(true);
    await new Promise((resolve) => setTimeout(resolve, 1800));
    try {
      // Create a document for each student
      const sharePromises = studentIds.map(async (studentId) => {
        const documentData = {
          sharedTo: studentId,
          comment: comment,
          fileUrl: fileUrl,
          transcriptContent: podcast.transcriptContent,
          shared: true,
          userId: user.uid,
          podcastUrl: podcast.podcastUrl,
          podcastName: podcast.podcastName,

          createdAt: Date.now(),

          speaker1: podcast.speaker1,
          speaker2: podcast.speaker2,
          topic: podcast.topic || "",
          originalPodcastId: podcastId,
        };
        // Create the podcast document and get its ID
        const podcastDoc = await addDoc(
          collection(db, "podcasts"),
          documentData,
        );
        Logger.log(
          "Adding document for student",
          studentId,
          ":",
          JSON.stringify(documentData, null, 2),
        );
        // Create notification with the podcast doc ID
        await addDoc(collection(db, "notifications"), {
          type: "podcast_shared",
          recipientId: studentId,
          senderId: user.uid,
          read: false,
          createdAt: serverTimestamp(),
          podcastData: {
            podcastId: podcastDoc.id,
            podcastName: documentData.podcastName,
            podcastUrl: documentData.podcastUrl,
            comment: documentData.comment,
            topic: documentData.topic,
          },
        });
        return podcastDoc;
      });
      await Promise.all(sharePromises);
      Logger.log("Podcast shared saved to DB");
      showSnackbar("Du har nu delat podcasten!", "success");
      // Close the share dialog
      setShareDialogOpen(false);
      setShareDialogOpenFriends(false);
    } catch (error) {
      Logger.error("Error sharing podcast:", error);
      showSnackbar("Ett fel uppstod när podcasten skulle delas.", "error");
    } finally {
      setIsSharing(false);
    }
  };

  const handleShareMenuOpen = (event) => {
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareMenuClose = () => {
    setShareAnchorEl(null);
  };

  // Parse transcript data
  // Parse transcript data
  const parseTranscript = (transcript) => {
    if (!transcript) return [];

    const regex = /<Person(\d+)>\s*"(.*?)"\s*<\/Person\d+>/g;
    const messages = [];
    let match;

    while ((match = regex.exec(transcript)) !== null) {
      const personNumber = match[1];
      const messageText = match[2].trim();
      messages.push({
        person: personNumber,
        text: messageText,
      });
    }

    return messages;
  };

  useEffect(() => {
    const fetchPodcast = async () => {
      try {
        const docRef = doc(db, "podcasts", podcastId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setPodcast(docSnap.data());
        } else {
          setError("Podcast not found");
        }
      } catch (err) {
        console.error("Error fetching podcast:", err);
        setError("Failed to load podcast");
      } finally {
        setLoading(false);
      }
    };

    if (podcastId) {
      fetchPodcast();
    }
  }, [podcastId]);

  useEffect(() => {
    if (audioRef.current) {
      const audio = audioRef.current; // Store reference to avoid null issues during cleanup

      const updateProgress = () => {
        if (audio) {
          // Add an additional check just to be safe
          setCurrentTime(audio.currentTime);
        }
      };

      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        if (audio) {
          // Add an additional check just to be safe
          audio.currentTime = 0;
        }
      };

      audio.addEventListener("timeupdate", updateProgress);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("ended", handleEnded);

      return () => {
        // Use the stored reference for cleanup
        audio.removeEventListener("timeupdate", updateProgress);
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("ended", handleEnded);
      };
    }
  }, [podcast]);

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e) => {
    if (audioRef.current) {
      const seekTime =
        (e.nativeEvent.offsetX / e.target.clientWidth) * duration;
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const getSpeakerInfo = (personNumber) => {
    if (!podcast) return { name: `Speaker ${personNumber}`, avatar: null };

    const speakerId =
      personNumber === "1" ? podcast.speaker1 : podcast.speaker2;
    const speakerData = availableSpeakers.find((s) => s.id === speakerId);

    return (
      speakerData || {
        name: personNumber === "1" ? "Speaker 1" : "Speaker 2",
        avatar: null,
      }
    );
  };

  const getSpeakerColor = (personNumber) => {
    return personNumber === "1"
      ? theme.palette.primary.main
      : theme.palette.secondary.main;
  };

  // Replace your current handleDownload function with this:
  const handleDownload = async () => {
    if (podcast && podcast.podcastUrl) {
      try {
        // Show loading indicator

        // Fetch the file to handle CORS issues
        const response = await fetch(podcast.podcastUrl);
        const blob = await response.blob();

        // Create an object URL for the blob
        const url = window.URL.createObjectURL(blob);

        // Create an anchor element and set properties
        const link = document.createElement("a");
        link.href = url;
        link.download = podcast.podcastName
          ? `${podcast.podcastName}.mp3`
          : `Podcast_studera_ai_${podcastId}.mp3`;

        // Append to the document, click and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL
        window.URL.revokeObjectURL(url);

        // Show success message
        showSnackbar("Podcasten laddas ned", "success");
      } catch (error) {
        console.error("Download error:", error);
        showSnackbar("Ett fel uppstod vid nedladdning av podcasten", "error");
      } finally {
      }
    }
  };

  if (loading) {
    return (
      <Box sx={styles.container}>
        <Box
          sx={{
            ...styles.innerContainer,
            width: { xs: "100%", md: "100%" },
            margin: "0 auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={styles.container}>
        <Box
          sx={{
            ...styles.innerContainer,
            width: { xs: "100%", md: "100%" },
            margin: "0 auto",
          }}
        >
          <Typography variant="h1" gutterBottom sx={{ ...styles.newH1, pt: 1 }}>
            Error
          </Typography>
          <Paper sx={{ ...styles.freshBox, ...styles.softBoxShadow, p: 4 }}>
            <Typography variant="h6" color="error">
              {error}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ mt: 2 }}
            >
              Go Back
            </Button>
          </Paper>
        </Box>
      </Box>
    );
  }

  const messages = parseTranscript(podcast?.transcriptContent || "");

  return (
    <Box sx={styles.container}>
      <Box
        sx={{
          ...styles.innerContainer,
          width: { xs: "100%", md: "100%" },
          margin: "0 auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 1,
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              mr: 2,
              color: theme.palette.text.primary,
              display: { xs: "none", md: "flex" },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography
            variant="h1"
            sx={{
              ...styles.newH1,
              textAlign: { xs: "center", md: "left" },
              width: "100%",
              marginTop: "0px",
            }}
          >
            {podcast.podcastName}
          </Typography>
        </Box>

        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            mr: 2,
            color: theme.palette.text.primary,
            display: { xs: "block", md: "none" },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Divider sx={{ mb: 3, display: "none" }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
          }}
        >
          {/* Player Column */}
          <Box
            sx={{
              flex: { xs: "1 1 100%", md: "2 1 0" },

              maxHeight: { xs: "400px", md: "400px" },
            }}
          >
            <Paper
              sx={{
                ...styles.freshBox,
                boxShadow: "none",

                p: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                mb: 4,
              }}
            >
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Podcast
              </Typography>

              {podcast?.podcastUrl ? (
                <>
                  <Box
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(0, 0, 0, 0.2)"
                          : "rgba(0, 0, 0, 0.05)",
                      borderRadius: 2,
                      p: 3,
                      mb: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <audio
                      ref={audioRef}
                      src={podcast.podcastUrl}
                      style={{ display: "none" }}
                    />

                    <Box
                      sx={{
                        width: "100%",
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="subtitle1">
                        {formatTime(currentTime)}
                      </Typography>
                      <Typography variant="subtitle1">
                        {formatTime(duration)}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        width: "100%",
                        height: 8,
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.1)",
                        borderRadius: 4,
                        position: "relative",
                        cursor: "pointer",
                        mb: 3,
                      }}
                      onClick={handleSeek}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          height: "100%",
                          width: `${(currentTime / duration) * 100}%`,
                          backgroundColor: theme.palette.primary.main,
                          borderRadius: 4,
                        }}
                      />
                    </Box>

                    <IconButton
                      onClick={togglePlayPause}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                        ...styles.softBoxShadow,
                        width: 64,
                        height: 64,
                      }}
                    >
                      {isPlaying ? (
                        <PauseIcon sx={{ fontSize: 32 }} />
                      ) : (
                        <PlayArrowIcon sx={{ fontSize: 32 }} />
                      )}
                    </IconButton>
                  </Box>

                  <Box sx={{ mt: "auto" }}>
                    <Divider sx={{ mb: 3 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 2,
                      }}
                    >
                      <Button
                        variant="print"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        fullWidth
                      >
                        Ladda ned
                      </Button>
                      <Button
                        variant="print"
                        startIcon={<ShareIcon />}
                        onClick={handleShareMenuOpen}
                        fullWidth
                      >
                        Dela
                      </Button>
                    </Box>
                  </Box>
                </>
              ) : (
                <Typography
                  variant="body1"
                  sx={{ textAlign: "center", color: "text.secondary" }}
                >
                  No audio available for this podcast.
                </Typography>
              )}
            </Paper>
            <Box
              sx={{
                ...styles.freshBox,
                backgroundColor: "#805ff429",
                boxShadow: "none",
                border: "none",
                padding: "0px",
                display: { xs: "none", md: "block" },
              }}
            >
              <BouncingSpeakers
                speaker1={podcast.speaker1}
                speaker2={podcast.speaker2}
                availableSpeakers={availableSpeakers}
                theme={theme}
                isPlaying={isPlaying}
                styles={styles}
              />
            </Box>
          </Box>
          {/* Transcript Column */}
          <Box
            sx={{
              flex: { xs: "1 1 100%", md: "3 1 0" },

              //overflowY: "auto",
            }}
          >
            <Paper
              sx={{
                ...styles.freshBox,
                boxShadow: "none",
                p: 3,
                mb: 3,
                height: "100%",
                mt: { xs: 8, md: 0 },
              }}
            >
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Konversation
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {messages.length > 0 ? (
                  messages.map((message, index) => {
                    const speakerInfo = getSpeakerInfo(message.person);
                    const isFirstPerson = message.person === "1";
                    const messageAlign = isFirstPerson
                      ? "flex-start"
                      : "flex-end";
                    const bubbleColor = isFirstPerson
                      ? isDark
                        ? "rgba(25, 118, 210, 0.15)"
                        : "rgb(153 153 153 / 10%)"
                      : isDark
                        ? "rgba(156, 39, 176, 0.15)"
                        : "rgb(223 221 249 / 55%)";

                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: messageAlign,
                          maxWidth: "100%",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            mb: 1,
                            gap: 1,
                          }}
                        >
                          {isFirstPerson && (
                            <Avatar
                              src={speakerInfo.avatar}
                              alt={speakerInfo.name}
                              sx={{ width: 36, height: 36 }}
                            />
                          )}
                          <Box
                            sx={{
                              backgroundColor: bubbleColor,
                              borderRadius: 2,
                              py: 1.5,
                              px: 2,
                              maxWidth: "92%",

                              position: "relative",
                              "&:after": isFirstPerson
                                ? {
                                    content: '""',
                                    position: "absolute",
                                    top: 15,
                                    left: -8,
                                    width: 0,
                                    height: 0,
                                    borderTop: "8px solid transparent",
                                    borderRight: `8px solid ${bubbleColor}`,
                                    borderBottom: "8px solid transparent",
                                  }
                                : {
                                    content: '""',
                                    position: "absolute",
                                    top: 15,
                                    right: -8,
                                    width: 0,
                                    height: 0,
                                    borderTop: "8px solid transparent",
                                    borderLeft: `8px solid ${bubbleColor}`,
                                    borderBottom: "8px solid transparent",
                                  },
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                color: theme.palette.text.primary,
                                fontWeight: 400,
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {message.text}
                            </Typography>
                          </Box>
                          {!isFirstPerson && (
                            <Avatar
                              src={speakerInfo.avatar}
                              alt={speakerInfo.name}
                              sx={{ width: 36, height: 36 }}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ textAlign: "center", color: "text.secondary" }}
                  >
                    No transcript available for this podcast.
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={shareAnchorEl}
        open={Boolean(shareAnchorEl)}
        onClose={handleShareMenuClose}
        PaperProps={{
          sx: {
            ...styles.roundMenu,
            ...styles.roundMenuLarger,
          },
        }}
      >
        {user.accountType === "teacher" && (
          <MenuItem
            onClick={() => {
              setShareDialogOpen(true);
              handleShareMenuClose();
            }}
            sx={{
              ...styles.menuItemStyle,
              ...styles.menuItemStyleLarger,
            }}
          >
            <ListItemIcon sx={{ mr: 1 }}>
              <SchoolIcon style={{ fontSize: "1.5rem" }} />
            </ListItemIcon>
            <ListItemText
              primary="Dela med elever"
              secondary="Dela med elever från ett av dina elevrum"
            />
          </MenuItem>
        )}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setShareLinkDialogOpen(true);
            handleShareMenuClose();
          }}
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
          }}
        >
          <ListItemIcon sx={{ mr: 1 }}>
            <LinkIcon style={{ fontSize: "1.5rem" }} />
          </ListItemIcon>
          <ListItemText
            primary="Dela länk"
            secondary="Kopiera en delbar länk till podcasten"
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setShareDialogOpenFriends(true);
            handleShareMenuClose();
          }}
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
          }}
        >
          <ListItemIcon sx={{ mr: 1 }}>
            <PeopleIcon style={{ fontSize: "1.5rem" }} />
          </ListItemIcon>
          <ListItemText
            primary="Dela med vänner"
            secondary="Dela med vänner från din vänlista"
          />
        </MenuItem>
      </Menu>

      <LoadingBackdrop
        isOpen={isSharing}
        imagePath="/img/AI.png"
        loadingText="Delar podcast..."
      />

      <SharePodcastDialog
        open={shareLinkDialogOpen}
        onClose={() => setShareLinkDialogOpen(false)}
        podcastId={podcastId}
      />

      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        onShare={handleSharePodcast}
        user={user}
      />

      <ShareDialog
        open={shareDialogOpenFriends}
        onClose={() => setShareDialogOpenFriends(false)}
        onShare={handleSharePodcast}
        user={user}
        theme={theme}
        shareWithFriends={true}
      />
    </Box>
  );
};

export default PodcastDetailsPage;
